/**
 * Single source of truth for whether the current user should see the
 * "JSON Config" block on the Config Template detail modal. Mirrors the exact
 * rules used by useRawTabVisible so both gates behave consistently:
 *
 *   1. Wait until UiVisibilityContext.ready resolves.
 *   2. If `json_config_usernames` is non-empty, only those usernames see it.
 *   3. Otherwise fall back to the global/role `show_json_config` boolean.
 *   4. Unset everywhere → default visible (no behaviour change).
 *
 * Returns `null` while the resolution is in flight so callers can keep
 * rendering everything else without painting the JSON Config block early.
 */

import { useAuth } from '../contexts/Auth/authContext';
import { useUiVisibility } from '../contexts/UiVisibilityContext';

export const useJsonConfigVisible = (): boolean | null => {
  const { user } = useAuth();
  const { showJsonConfig, jsonConfigUsernames, ready } = useUiVisibility();

  if (!ready) return null;

  const username = user?.preferredUsername ?? null;
  if (jsonConfigUsernames.length > 0) {
    return !!username && jsonConfigUsernames.includes(username);
  }
  return showJsonConfig;
};
